import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import {
  X,
  Sparkles,
  Zap,
  Coins,
  Clock,
  ArrowUpRight,
  type LucideIcon,
} from "lucide-react";
import { Portal } from "../portal";
import { cn } from "@/lib/utils";
import {
  useUserStore,
  type PlanType,
  type LegacyPlan,
  getPlanDetails,
  convertApiPlanToLegacy,
} from "@/stores/user-store";
import { PlanCard } from "@/components/features/billing";
import { usePlans, usePayments } from "@/hooks";
import { isMaxPlan } from "@/config/plan-utils";
import { AnalyticsService } from "@/services/analytics";
import { useUser } from "@clerk/clerk-react";
import { useUserProfile } from "@/hooks/use-user";

export type UpgradePreset = "upgrade" | "no-credits" | "concurrent-limit";

interface PresetConfig {
  icon: LucideIcon;
  iconContainerClass: string;
  title: string;
  description: ReactNode;
  accentGradient: string;
  allowedPlans?: PlanType[];
  highlightBenefit?: string;
  featuredPlan?: PlanType | "auto";
  infoBanner?: ReactNode;
}

const getPresetConfig = (
  preset: UpgradePreset,
  context?: {
    creditsNeeded?: number;
    currentCredits?: number;
    feature?: string;
  }
): PresetConfig => {
  switch (preset) {
    case "no-credits":
      return {
        icon: Coins,
        iconContainerClass: "bg-amber-500/20 border border-amber-500/30",
        title: "You Need More Credits",
        description: (
          <>
            This generation requires{" "}
            <span className="text-amber-400 font-semibold">
              {context?.creditsNeeded ?? 0} credits
            </span>{" "}
            but you only have{" "}
            <span className="text-white font-semibold">
              {context?.currentCredits ?? 0}
            </span>
            .
          </>
        ),
        accentGradient:
          "bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500",
      };
    case "concurrent-limit":
      return {
        icon: Zap,
        iconContainerClass:
          "bg-gradient-to-br from-amber-500/20 to-violet-500/20 border border-white/[0.1]",
        title: "Generation In Progress",
        description: (
          <>
            You already have a generation running. Upgrade to{" "}
            <span className="text-violet-400 font-medium">Pro</span> or{" "}
            <span className="text-amber-400 font-medium">Ultimate</span> for
            unlimited concurrent generations.
          </>
        ),
        accentGradient:
          "bg-gradient-to-r from-amber-500 via-violet-500 to-violet-600",
        allowedPlans: ["pro", "ultimate"] as PlanType[],
        highlightBenefit: "Unlimited concurrent generations",
        infoBanner: (
          <div className="mx-4 sm:mx-6 mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="size-4 text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">
                  Your current plan allows 1 generation at a time
                </p>
                <p className="text-xs text-[#888] mt-0.5">
                  Wait for your current generation to complete, or upgrade for
                  parallel processing
                </p>
              </div>
            </div>
          </div>
        ),
      };
    case "upgrade":
    default:
      return {
        icon: Sparkles,
        iconContainerClass:
          "bg-gradient-to-br from-violet-500/20 to-amber-500/20 border border-white/[0.1]",
        title: context?.feature
          ? `Unlock ${context.feature}`
          : "Upgrade Your Plan",
        description:
          "Get more credits, longer videos, and premium features to bring your creative vision to life.",
        accentGradient:
          "bg-gradient-to-r from-amber-500 via-violet-500 to-violet-600",
      };
  }
};

export interface UpgradePlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  preset?: UpgradePreset;
  presetContext?: {
    creditsNeeded?: number;
    currentCredits?: number;
    feature?: string;
  };
  icon?: LucideIcon;
  iconContainerClass?: string;
  title?: string;
  description?: ReactNode;
  accentGradient?: string;
  allowedPlans?: PlanType[];
  infoBanner?: ReactNode;
  highlightBenefit?: string;
  featuredPlan?: PlanType | "auto";
  featuredLabel?: string;
}

export function UpgradePlansModal({
  isOpen,
  onClose,
  preset = "upgrade",
  presetContext,
  icon: iconOverride,
  iconContainerClass: iconContainerClassOverride,
  title: titleOverride,
  description: descriptionOverride,
  accentGradient: accentGradientOverride,
  allowedPlans: allowedPlansOverride,
  infoBanner: infoBannerOverride,
}: UpgradePlansModalProps) {
  const presetConfig = getPresetConfig(preset, presetContext);
  const Icon = iconOverride ?? presetConfig.icon;
  const iconContainerClass =
    iconContainerClassOverride ?? presetConfig.iconContainerClass;
  const title = titleOverride ?? presetConfig.title;
  const description = descriptionOverride ?? presetConfig.description;
  const accentGradient = accentGradientOverride ?? presetConfig.accentGradient;
  const allowedPlans = allowedPlansOverride ?? presetConfig.allowedPlans;
  const infoBanner = infoBannerOverride ?? presetConfig.infoBanner;

  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnnual, setIsAnnual] = useState(true);
  const { plan: currentPlan } = useUserStore();
  const { data: plansData } = usePlans();
  const { user: clerkUser } = useUser();
  // Full backend user — carries the signed PieCan referral URL + upload gate
  // used by the secondary "Try PieCan" CTA below.
  const { user: profileUser, isSignedIn } = useUserProfile();
  const piecanReferralUrl =
    isSignedIn && profileUser?.can_upload_images
      ? profileUser.piecan_referral_url
      : null;
  const hasTrackedOpenRef = useRef(false);
  const dragStartY = useRef(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    cryptoLoading,
    telegramLoading,
    handleCryptoPurchase,
    handleTelegramPurchase,
    handleCardPurchase,
  } = usePayments({
    userId: clerkUser?.id,
    isSignedIn: !!clerkUser,
  });

  const DISMISS_THRESHOLD = 100;

  useEffect(() => {
    if (isOpen) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    const isHandle = target.closest("[data-handle]");
    const isScrolledToTop = contentRef.current
      ? contentRef.current.scrollTop === 0
      : true;

    if (isHandle || isScrolledToTop) {
      dragStartY.current = e.touches[0].clientY;
      setIsDragging(true);
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;

      const currentY = e.touches[0].clientY;
      const diff = currentY - dragStartY.current;

      if (diff > 0) {
        setDragY(diff);
      }
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    if (dragY > DISMISS_THRESHOLD) {
      onClose();
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY, onClose]);

  useEffect(() => {
    if (isOpen && !hasTrackedOpenRef.current) {
      AnalyticsService.trackAddToCart(clerkUser?.id);
      hasTrackedOpenRef.current = true;
    }
    if (!isOpen) {
      hasTrackedOpenRef.current = false;
    }
  }, [isOpen, clerkUser?.id]);

  const noUpgradesAvailable = isMaxPlan(currentPlan);

  const ALL_PAID_PLANS: PlanType[] = ["starter", "creator", "pro", "ultimate"];
  const MAIN_PLAN_ORDER: PlanType[] = ["starter", "creator", "pro"];

  const upgradePlans = useMemo(() => {
    const hierarchy: PlanType[] = [
      "free",
      "starter",
      "creator",
      "pro",
      "ultimate",
    ];
    const plansToFilter = allowedPlans ?? ALL_PAID_PLANS;
    return plansToFilter.filter(
      (plan) => hierarchy.indexOf(plan) > hierarchy.indexOf(currentPlan)
    );
  }, [currentPlan, allowedPlans]);

  const filteredPlans = useMemo(() => {
    if (noUpgradesAvailable || upgradePlans.length === 0) return [];

    if (plansData?.plans) {
      const plansToUse = plansData.plans.filter((p) =>
        isAnnual ? p.billing_period_days === 365 : p.billing_period_days === 30
      );
      return plansToUse
        .map(convertApiPlanToLegacy)
        .filter((p) => upgradePlans.includes(p.id));
    }

    return upgradePlans
      .map((id) => getPlanDetails(id))
      .filter(Boolean) as LegacyPlan[];
  }, [plansData, upgradePlans, isAnnual, noUpgradesAvailable]);

  const { mainPlans, ultimatePlanData } = useMemo(() => {
    const main = MAIN_PLAN_ORDER
      .map((id) => filteredPlans.find((p) => p.id === id))
      .filter(Boolean) as LegacyPlan[];
    const ultimate = filteredPlans.find((p) => p.id === "ultimate") ?? null;
    return { mainPlans: main, ultimatePlanData: ultimate };
  }, [filteredPlans]);

  const handleBillingToggle = () => setIsAnnual((prev) => !prev);

  const getPlanId = (planType: string): string => {
    return isAnnual ? `${planType}_annual` : `${planType}_monthly`;
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm transition-opacity duration-200"
        style={{ opacity: Math.max(0, 1 - dragY / 300) }}
        onClick={onClose}
      />

      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none overflow-y-auto">
        <div
          className={cn(
            "relative w-full max-w-5xl sm:my-8 bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-white/[0.1] rounded-t-2xl sm:rounded-2xl shadow-2xl pointer-events-auto overflow-hidden h-[calc(100vh-2rem)] sm:h-auto sm:max-h-[calc(100vh-4rem)] flex flex-col",
            "will-change-transform touch-none sm:touch-auto",
            !isDragging && "transition-transform duration-300 ease-out"
          )}
          style={{
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            data-handle
            className="flex justify-center pt-3 pb-1 sm:hidden cursor-grab active:cursor-grabbing"
          >
            <div className="w-10 h-1 rounded-full bg-white/20" />
          </div>

          <div
            className={cn(
              "hidden sm:block absolute top-0 left-0 right-0 h-1",
              accentGradient
            )}
          />

          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-2 rounded-lg text-[#666] hover:text-white hover:bg-white/[0.05] transition-colors"
          >
            <X className="size-5" />
          </button>

          {/* Header */}
          <div className="px-4 sm:px-6 pt-6 sm:pt-8 pb-4 sm:pb-6 text-center shrink-0">
            <div
              className={cn(
                "inline-flex p-2.5 sm:p-3 rounded-2xl mb-3 sm:mb-4",
                iconContainerClass
              )}
            >
              <Icon className="size-5 sm:size-6 text-violet-400" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
              {title}
            </h2>
            <p className="text-sm sm:text-base text-[#888] max-w-md mx-auto">
              {description}
            </p>
          </div>

          {infoBanner}

          {/* Plan cards */}
            <div
            ref={contentRef}
            className="px-4 sm:px-6 pt-1 pb-6 sm:pb-8 overflow-y-auto flex-1 overscroll-contain touch-pan-y"
          >
            {noUpgradesAvailable ? (
              <div className="text-center py-8">
                <p className="text-[#888]">You're on the highest plan!</p>
              </div>
            ) : (
              <>
                {mainPlans.length > 0 && (
                  <div className={cn(
                    "grid gap-4 mb-4 pt-3",
                    mainPlans.length === 1 ? "grid-cols-1 max-w-sm mx-auto" :
                    mainPlans.length === 2 ? "grid-cols-1 sm:grid-cols-2" :
                    "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                  )}>
                    {mainPlans.map((plan) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        isAnnual={isAnnual}
                        onBillingToggle={handleBillingToggle}
                        isCurrentPlan={currentPlan === plan.id}
                        onCryptoPurchase={() => handleCryptoPurchase(getPlanId(plan.id), plan.name)}
                        onTelegramPurchase={() => handleTelegramPurchase(getPlanId(plan.id), plan.name)}
                        onCardPurchase={() => handleCardPurchase(getPlanId(plan.id), plan.name)}
                        cryptoLoading={cryptoLoading === getPlanId(plan.id)}
                        telegramLoading={telegramLoading === getPlanId(plan.id)}
                      />
                    ))}
                  </div>
                )}

                {ultimatePlanData && (
                  <PlanCard
                    plan={ultimatePlanData}
                    isAnnual={isAnnual}
                    onBillingToggle={handleBillingToggle}
                    isCurrentPlan={currentPlan === "ultimate"}
                    onCryptoPurchase={() => handleCryptoPurchase(getPlanId("ultimate"), ultimatePlanData.name)}
                    onTelegramPurchase={() => handleTelegramPurchase(getPlanId("ultimate"), ultimatePlanData.name)}
                    onCardPurchase={() => handleCardPurchase(getPlanId("ultimate"), ultimatePlanData.name)}
                    cryptoLoading={cryptoLoading === getPlanId("ultimate")}
                    telegramLoading={telegramLoading === getPlanId("ultimate")}
                    isUltimate
                  />
                )}
              </>
            )}

            {/* Secondary partner CTA — only for upload-capable users who have a
                signed PieCan referral URL. Omitted entirely otherwise so it
                never shows a dead link. First PieCan sign-in via this link
                grants tier 1 automatically. */}
            {piecanReferralUrl && (
              <div className="mt-4 pt-4 border-t border-white/[0.06] text-center">
                <a
                  href={piecanReferralUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    AnalyticsService.trackPiecanHandoffClick(
                      profileUser?.user_id,
                      "gating_modal",
                    )
                  }
                  className="group inline-flex items-center gap-1.5 text-sm text-violet-300 hover:text-violet-200 transition-colors"
                >
                  Prefer image-to-video? Try{" "}
                  <span className="font-semibold text-white">PieCan</span> — tier
                  1 access automatically
                  <ArrowUpRight className="size-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
